import React, { useMemo } from 'react'
import { useTable, useFlexLayout } from 'react-table'
import { useSticky } from 'react-table-sticky'
import {
  Box,
  Table as MuiTable,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  TableContainer,
  Paper,
  TableSortLabel,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import T from 'prop-types'
import theme from 'theme'

import TableBodyNotLoaded from 'components/atoms/TableBodyNotLoaded'

import TablePagination from './TablePagination'

const StyledHeadCell = styled(TableCell)(({ theme }) => ({
  fontSize: theme.typography.caption.fontSize,
  // fontWeight: 'bold',
  color: theme.palette.text.secondary,
  whiteSpace: 'nowrap',
}))

const Root = styled(MuiTable)(({ theme }) => ({
  '& [data-sticky-td]': {
    position: 'absolute',
    backgroundColor: theme.palette.background.default,
  },
  '& .MuiTableRow-hover:hover [data-sticky-td]': {
    backgroundColor: theme.custom.hover,
  },
  '& [data-sticky-last-left-td]': {
    borderRight: `1px solid ${theme.custom.boxShadowColor}`,
    boxShadow: theme.custom.boxShadowRight,
  },
}))

const rowHeight = 37

// eslint-disable-next-line sonarjs/cognitive-complexity
export default function Table({
  id,
  isFetching,
  error,
  items = [],
  columns,
  total,
  getRowProps = () => ({}),
  cellProps,
  headerProps,
  hasPagination = false,
  isFetchingOtherPages = false,
  rowsPerPage = 10,
  rowsPerPageOptions = [10, 25, 50, 100],
  page = 0,
  handleChangePage = () => {},
  handleChangeRowsPerPage = () => {},
  sortable,
  excludedSortableIds,
  orderColumn,
  orderDirection,
  handleSort = () => {},
  stickyHeader,
  bodyMaxHeight,
  top,
  bottom,
  tablePaginationProps,
}) {
  const bodyIsNotLoaded = isFetching || error || items.length === 0

  const someSticky = useMemo(() => columns.some((col) => col.sticky), [columns])

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data: items || [],
      },
      someSticky ? useFlexLayout : undefined,
      useSticky
    )

  const emptyRows = useMemo(
    () =>
      hasPagination && !bodyIsNotLoaded
        ? rowsPerPage - Math.min(rowsPerPage, total - page * rowsPerPage)
        : 0,
    [bodyIsNotLoaded, hasPagination, page, rowsPerPage, total]
  )

  return (
    <TableContainer
      component={Paper}
      sx={{
        overflowX: 'hidden',
        mb: hasPagination ? 7.5 : undefined,
      }}
    >
      {top && (
        <Box
          sx={{
            position: someSticky ? 'sticky' : undefined,
            left: someSticky ? 0 : undefined,
          }}
        >
          {top}
        </Box>
      )}
      <Box
        sx={{
          overflowX: 'scroll',
          ...(bodyMaxHeight
            ? { overflowY: 'auto', maxHeight: bodyMaxHeight }
            : {}),
        }}
      >
        <Root
          data-testid={id || 'custom-table'}
          role="table"
          sx={{
            minHeight: hasPagination
              ? rowHeight * (rowsPerPage + 1) + 52 * isFetching
              : null,
          }}
          stickyHeader
          size="small"
          {...getTableProps()}
        >
          <TableHead
            sx={{
              backgroundColor: theme.palette.background.default,
              ...(stickyHeader
                ? { position: 'sticky', top: '0', zIndex: '4' }
                : {}),
            }}
          >
            {headerGroups.map((headerGroup, i) => {
              return (
                <TableRow
                  {...headerGroup.getHeaderGroupProps()}
                  key={`headerGroup_${i}`}
                >
                  {headerGroup.headers.map((column) => {
                    return (
                      <StyledHeadCell
                        {...cellProps}
                        {...headerProps}
                        {...column.getHeaderProps()}
                        {...column.cellProps}
                        {...column.headerProps}
                        data-testid={`column-${column.id}`}
                        key={column.id}
                      >
                        {sortable &&
                        (!excludedSortableIds ||
                          !excludedSortableIds.includes(column.id)) ? (
                          <TableSortLabel
                            data-testid={`sort-${column.id}`}
                            active={orderColumn === column.id}
                            direction={
                              orderColumn === column.id && orderDirection
                                ? orderDirection.toLowerCase()
                                : 'asc'
                            }
                            onClick={() => {
                              handleSort(column.id)
                            }}
                          >
                            {column.render('Header')}
                          </TableSortLabel>
                        ) : (
                          column.render('Header')
                        )}
                      </StyledHeadCell>
                    )
                  })}
                </TableRow>
              )
            })}
          </TableHead>

          <TableBody {...getTableBodyProps()}>
            {bodyIsNotLoaded ? (
              <TableBodyNotLoaded
                error={error}
                isFetching={isFetching}
                colSpan={headerGroups[0].headers.length}
              />
            ) : (
              rows.map((row) => {
                prepareRow(row)
                return (
                  <TableRow
                    hover
                    tabIndex={-1}
                    data-testid={`table-row-${row.id}`}
                    {...row.getRowProps(getRowProps(row))}
                    key={row.id}
                  >
                    {row.cells.map((cell) => {
                      return (
                        <TableCell
                          // component={cell.column.sticky ? 'th' : 'td'}
                          // sx={{
                          // whiteSpace: 'nowrap',
                          // }}
                          {...cellProps}
                          {...cell.getCellProps()}
                          {...cell.column.cellProps}
                          sx={{
                            ...cellProps?.sx,
                            ...cell.getCellProps()?.sx,
                            ...cell.column.cellProps?.sx,
                          }}
                          key={`${row.id}_${cell.column.id}`}
                        >
                          {cell.render('Cell')}
                        </TableCell>
                      )
                    })}
                  </TableRow>
                )
              })
            )}
            {emptyRows > 0 && !isFetching && (
              <TableRow style={{ height: rowHeight * emptyRows }}>
                <TableCell colSpan={headerGroups[0].headers.length} />
              </TableRow>
            )}
          </TableBody>
        </Root>
      </Box>
      {hasPagination && !isFetching && (
        <TablePagination
          isFetching={isFetchingOtherPages}
          total={total}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={rowsPerPageOptions}
          page={page}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          {...tablePaginationProps}
        />
      )}
      {bottom && (
        <Box
          sx={{
            position: someSticky ? 'sticky' : undefined,
            left: someSticky ? 0 : undefined,
          }}
        >
          {bottom}
        </Box>
      )}
    </TableContainer>
  )
}

Table.propTypes = {
  isFetching: T.bool,
  error: T.object,
  items: T.array.isRequired,
  columns: T.array.isRequired,
  getRowProps: T.func,
  cellProps: T.object,
  headerProps: T.object,
  hasPagination: T.bool,
  isFetchingOtherPages: T.bool,
  total: T.number,
  rowsPerPage: T.number,
  rowsPerPageOptions: T.array,
  page: T.number,
  handleChangePage: T.func,
  handleChangeRowsPerPage: T.func,
  sortable: T.bool,
  excludedSortableIds: T.array,
  orderColumn: T.oneOfType([T.string, T.number]),
  orderDirection: T.string,
  handleSort: T.func,
  stickyHeader: T.bool,
  bodyMaxHeight: T.string,
  top: T.node,
  bottom: T.node,
  tablePaginationProps: T.object,
}
